/**
 * The devices panel in the account dialog (D171): the shell's stateful
 * half of the list, the history, and the two ways to end a session.
 *
 * @remarks
 * Asked of the identity once the account is known: every session of the
 * account and its sign-ins, read together and shown as one panel. Ending
 * another device re-reads the list; ending THIS device (there is no button
 * for it here — that is Sign out) is the one change that hands control to
 * `onIdentityChanged`, because the open session belonged to someone who
 * is no longer signed in. Refusals are said through the dialog's own
 * notice. An identity that cannot answer leaves the panel off: nothing
 * true to say, nothing said.
 *
 * @packageDocumentation
 */

import type { DeviceSession, SignInEvent } from '@orpheus/api-contracts';
import type { AccountNotice, DevicesPanelView } from '@orpheus/player-ui';

import { useEffect, useState } from 'react';

import { describeUserAgent, deviceDetailOf, DEVICES_LABELS, DEVICES_NOTICE, signInLineOf } from './device-copy.js';
import { type IdentityClient } from './identity-client.js';

/** What the hook needs. */
export interface DevicesPanelOptions {
	/** The signed-in account's id; absent, nothing is asked and nothing is shown. */
	readonly accountId: string | undefined;
	readonly identity: IdentityClient;
	/** Now, for "last seen 2 days ago" — handed in so the reading is testable; the shell passes the clock. */
	readonly nowMs: () => number;
	/** Say a line through the dialog's notice — what was done, or why it was not (D172: the kind colors it). */
	readonly say: (notice: AccountNotice) => void;
}

/** What the identity said, once asked. */
interface Answer {
	readonly devices: readonly DeviceSession[];
	readonly signIns: readonly SignInEvent[];
}

/**
 * The devices panel for the account dialog.
 *
 * @param options - {@link DevicesPanelOptions}
 * @returns The panel, or nothing to show
 */
export const useDevicesPanel = ({ accountId, identity, nowMs, say }: DevicesPanelOptions): DevicesPanelView | undefined => {
	const [answer, setAnswer] = useState<Answer | undefined>(undefined);
	const [isBusy, setIsBusy] = useState(false);
	// Bumped after a revoke, so the effect asks again without the account changing.
	const [reading, setReading] = useState(0);

	useEffect(() => {
		setAnswer(undefined);

		if (accountId === undefined) {
			return undefined;
		}

		let isDisposed = false;

		void Promise.all([identity.listDevices(), identity.readSignIns()])
			.then(([devices, signIns]) => {
				if (isDisposed) {
					return;
				}

				if (devices === undefined) {
					say({ kind: 'refused', line: DEVICES_NOTICE.refused.listDevices });

					return;
				}

				setAnswer({ devices, signIns: signIns ?? []});
			})
			// An identity that cannot be reached is a panel not shown, not a broken dialog.
			.catch(() => undefined);

		return () => {
			isDisposed = true;
		};
	}, [accountId, identity, reading, say]);

	if (answer === undefined) {
		return undefined;
	}

	const now = nowMs();

	/** Go through one revoke door: say the refusal, or read the list again. */
	const through = (door: () => Promise<{ readonly kind: 'done'; readonly revoked: number } | { readonly kind: 'refused'; readonly reason: string }>, onDone: (revoked: number) => void) => {
		setIsBusy(true);
		void door()
			.then((outcome) => {
				if (outcome.kind === 'refused') {
					say({ kind: 'refused', line: outcome.reason });
				} else {
					onDone(outcome.revoked);
				}
			})
			// A throw is the network, not a door: the identity could not be reached at all.
			.catch(() => {
				say({ kind: 'refused', line: DEVICES_NOTICE.refused.unreachable });
			})
			.finally(() => {
				setIsBusy(false);
				setReading((count) => count + 1);
			});
	};

	return {
		devices: answer.devices.map((device) => ({
			detail: deviceDetailOf(device, now),
			id: device.id,
			isCurrent: device.isCurrent,
			name: describeUserAgent(device.userAgent),
		})),
		history: answer.signIns.map((signIn) => ({ id: signIn.id, line: signInLineOf(signIn, now) })),
		isBusy,
		labels: DEVICES_LABELS,
		onRevoke: (id) => {
			through(async () => identity.revokeDevice(id), () => undefined);
		},
		onRevokeOthers: () => {
			through(async () => identity.revokeOtherDevices(), (revoked) => {
				say({ kind: 'said', line: DEVICES_NOTICE.revokedOthers(revoked) });
			});
		},
	};
};
